import { create } from "zustand";
import { env } from "@/config/env";
import i18n from "@/i18n";
import type { CombifyUser } from "@/types";
import { useChatStore } from "@/stores/chatStore";
import { useMapStore } from "@/stores/mapStore";
import { useUsageStore } from "@/stores/usageStore";
import { useToastStore } from "@/stores/toastStore";
import * as localAuth from "@/lib/localAuth";
import * as amplifyAuth from "aws-amplify/auth";

const auth = env.isDev ? localAuth : amplifyAuth;

const DEV_API_URL = "http://localhost:8000";

function getApiUrl(): string {
  return env.isDev ? DEV_API_URL : env.aws.apiUrl;
}

interface AuthState {
  user: CombifyUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  linkSent: boolean;
  linkEmail: string | null;
  requestMagicLink: (email: string) => Promise<void>;
  verifyMagicLink: (token: string) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  linkSent: false,
  linkEmail: null,

  requestMagicLink: async (email) => {
    set({ isLoading: true, error: null });
    try {
      if (env.isDev) {
        // Dev mode: simulate link sent, auto-login
        await localAuth.requestMagicLink(email);
        set({ linkSent: true, linkEmail: email, isLoading: false });
        return;
      }
      const res = await fetch(`${getApiUrl()}/auth/magic-link/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("requestFailed");
      set({ linkSent: true, linkEmail: email, isLoading: false });
    } catch {
      set({ error: "requestFailed", isLoading: false });
    }
  },

  loginWithPassword: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      if (env.isDev) {
        await localAuth.requestMagicLink(email);
        await get().checkAuth();
        return;
      }
      await amplifyAuth.signIn({ username: email, password });
      await get().checkAuth();
    } catch {
      set({ error: "loginError", isLoading: false });
    }
  },

  verifyMagicLink: async (token) => {
    set({ isLoading: true, error: null });
    try {
      if (env.isDev) {
        await localAuth.verifyMagicLink(token);
        await get().checkAuth();
        return;
      }
      const res = await fetch(`${getApiUrl()}/auth/magic-link/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (res.status === 401) {
        set({ error: "invalidToken", isLoading: false });
        return;
      }
      if (!res.ok) throw new Error("verifyFailed");

      const { access_token, id_token, refresh_token } = await res.json();

      // Decode sub (username) from id_token payload
      const payload = JSON.parse(atob(id_token.split(".")[1]));
      const username = payload.sub;
      const clientId = env.aws.cognito.userPoolClientId;
      const prefix = `CognitoIdentityServiceProvider.${clientId}`;

      // Write tokens into Amplify v6 localStorage format
      localStorage.setItem(`${prefix}.LastAuthUser`, username);
      localStorage.setItem(`${prefix}.${username}.idToken`, id_token);
      localStorage.setItem(`${prefix}.${username}.accessToken`, access_token);
      localStorage.setItem(`${prefix}.${username}.refreshToken`, refresh_token);
      localStorage.setItem(`${prefix}.${username}.clockDrift`, "0");

      await get().checkAuth();
    } catch {
      set({ error: "verifyFailed", isLoading: false });
    }
  },

  logout: async () => {
    await auth.signOut();
    set({
      user: null,
      isAuthenticated: false,
      error: null,
      linkSent: false,
      linkEmail: null,
    });
    useChatStore.getState().resetChat();
    useMapStore.getState().clearFeatures();
    useUsageStore.getState().reset();
  },

  checkAuth: async () => {
    const wasAuthenticated = get().user !== null;
    try {
      await auth.getCurrentUser();
      const raw = await auth.fetchUserAttributes();
      const attributes = raw as Record<string, string | undefined>;
      const user: CombifyUser = {
        userId: attributes["custom:user_id"] ?? attributes["sub"] ?? "",
        email: attributes.email ?? "",
        firstName: attributes.given_name ?? "",
        lastName: attributes.family_name ?? "",
        tier: attributes["custom:tier"] ?? "",
        maxTier: attributes["custom:max_tier"] ?? "",
        customerId: attributes["custom:customer_id"] ?? "",
        organizationId: attributes["custom:organization_id"] ?? "",
      };
      set({ user, isAuthenticated: true, isLoading: false, error: null });
    } catch {
      if (wasAuthenticated) {
        useToastStore.getState().addToast({
          type: "info",
          message: i18n.t("auth.sessionExpired"),
          duration: 6000,
        });
      }
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  getIdToken: async () => {
    try {
      const session = await auth.fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  },
}));
